import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Package, Printer, X, Truck, Boxes, MapPin, Layers, Ban, FileText, Settings, PackageCheck, Zap, TrendingUp, Globe } from "lucide-react";
import { api } from "@/lib/api";
import { Loading, PageHead, Empty, money } from "@/components/common";
import { StatusPill } from "@/components/StatusPill";

const CARRIERS = [
  "USPS", "UPS", "FedEx", "DHL Express", "Royal Mail", "Canada Post",
  "Australia Post", "Aramex", "PostNL", "India Post", "China Post", "EMS", "SF Express", "Other",
];

const TABS = [
  { key: "awaiting", label: "Awaiting Shipment" },
  { key: "shipped", label: "Shipped" },
  { key: "delivered", label: "Delivered" },
  { key: "insights", label: "Insights", noCount: true },
];

function printHtml(title, inner) {
  const w = window.open("", "_blank", "width=520,height=720");
  if (!w) { toast.error("Allow pop-ups to print"); return; }
  w.document.write(`<html><head><title>${title}</title><style>
    body{font-family:Arial,sans-serif;padding:24px;color:#10241c}
    .box{border:2px solid #0c4a38;border-radius:12px;padding:20px}
    h1{font-size:20px;margin:0 0 4px}.muted{color:#5c5c50;font-size:12px}
    .row{margin:10px 0}.big{font-size:22px;font-weight:800;letter-spacing:2px}
    .bar{height:56px;background:repeating-linear-gradient(90deg,#000 0 3px,#fff 3px 6px);margin-top:8px;border-radius:4px}
    table{width:100%;border-collapse:collapse;margin-top:8px}td,th{text-align:left;padding:6px 4px;border-bottom:1px solid #eee;font-size:13px}
  </style></head><body>${inner}<script>window.onload=()=>window.print()</script></body></html>`);
  w.document.close();
}

function addr(a) {
  if (!a) return "";
  return `${a.name || ""}<br/>${a.street1 || ""}${a.street2 ? " " + a.street2 : ""}<br/>${a.city || ""}, ${a.state || ""} ${a.zip || ""}`;
}

function printLabel(o) {
  if (o.label_url) { window.open(o.label_url, "_blank"); return; }
  const ret = o.return_to || o.ship_from;
  printHtml(`Label ${o.tracking_number}`, `<div class="box">
    <h1>${o.carrier || "Carrier"}</h1><div class="muted">${o.service || ""}</div>
    <div class="row"><div class="muted">SHIP TO</div><b>${addr(o.ship_to)}</b></div>
    ${o.ship_from ? `<div class="row"><div class="muted">FROM</div>${addr(o.ship_from)}</div>` : ""}
    ${ret ? `<div class="row"><div class="muted">RETURN ADDRESS</div>${addr(ret)}</div>` : ""}
    <div class="row"><div class="muted">ITEM</div>${o.item_title || ""}</div>
    <div class="row"><div class="muted">TRACKING</div><div class="big">${o.tracking_number || ""}</div><div class="bar"></div></div>
  </div>`);
}

async function printSlip(orderId) {
  try {
    const d = await api.get(`/shipping/packing-slip/${orderId}`);
    const rows = d.items.map((i) => `<tr><td>${i.title}</td><td style="text-align:right">${money(i.amount)}</td></tr>`).join("");
    printHtml("Packing slip", `<div class="box">
      <h1>Packing Slip</h1><div class="muted">Order ${d.order_id.slice(-8)}${d.tracking_number ? " · " + d.tracking_number : ""}</div>
      <div class="row"><div class="muted">SHIP TO</div><b>${addr(d.ship_to)}</b></div>
      <div class="row"><div class="muted">FROM</div>${addr(d.ship_from)}</div>
      <table><thead><tr><th>Item</th><th style="text-align:right">Price</th></tr></thead><tbody>${rows}</tbody></table>
    </div>`);
  } catch (e) { toast.error(e.message); }
}

function RateModal({ target, onClose, onDone }) {
  // target: { order_ids: [...], count, label } — fetch rates using the lead order
  const [rates, setRates] = useState(null);
  const [selected, setSelected] = useState(null);
  const [busy, setBusy] = useState(false);
  const isBundle = target.order_ids.length > 1;

  useEffect(() => {
    api.post("/shipping/rates", { order_id: target.order_ids[0] })
      .then((d) => { setRates(d.rates); setSelected(d.rates[0]?.rate_id); })
      .catch((e) => { toast.error(e.message); onClose(); });
  }, []);

  const buy = async () => {
    setBusy(true);
    try {
      const body = { order_ids: target.order_ids };
      if (!isBundle) body.rate = rates.find((r) => r.rate_id === selected);
      const res = await api.post("/shipping/label", body);
      toast.success(isBundle ? `Bundled label created for ${res.bundled} items` : "Shipping label created");
      onDone(res);
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  return (
    <div className="ss-modal-back" onClick={onClose}>
      <div className="ss-modal" onClick={(e) => e.stopPropagation()} data-testid="rate-modal">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
          <h2 style={{ fontSize: 20 }}>{isBundle ? `Buy 1 label for ${target.order_ids.length} items` : "Choose a shipping rate"}</h2>
          <button className="disc-search" style={{ width: 34, height: 34 }} onClick={onClose} data-testid="rate-modal-close"><X size={18} /></button>
        </div>
        {isBundle && <p className="ss-muted" style={{ fontSize: 13, marginBottom: 12 }}>Combined into one shipment — the buyer is charged shipping once. We'll pick the best rate for the combined weight.</p>}
        {rates === null ? <Loading /> : (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {!isBundle && rates.map((r) => (
              <label key={r.rate_id} className="ss-card" style={{ padding: 14, display: "flex", alignItems: "center", gap: 12, cursor: "pointer", borderColor: selected === r.rate_id ? "var(--brand)" : "var(--line)" }} data-testid={`rate-${r.carrier}-${r.service}`.replace(/\s+/g, "-")}>
                <input type="radio" name="rate" checked={selected === r.rate_id} onChange={() => setSelected(r.rate_id)} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 800 }}>{r.carrier} · {r.service}</div>
                  <div className="ss-muted" style={{ fontSize: 13 }}>{r.delivery_window}</div>
                </div>
                <div style={{ textAlign: "right" }}>
                  <div style={{ fontWeight: 800 }}>{money(r.buyer_charge)}</div>
                </div>
              </label>
            ))}
            <button className="ss-btn ss-btn-primary ss-btn-block" onClick={buy} disabled={busy} data-testid="buy-label-confirm">
              <Printer size={16} /> {busy ? "Creating label…" : isBundle ? "Create bundled label" : "Buy label & mark shipped"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function ManualTrackingModal({ order, onClose, onDone }) {
  const [carrier, setCarrier] = useState("DHL Express");
  const [custom, setCustom] = useState("");
  const [tracking, setTracking] = useState("");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    const tn = tracking.trim();
    if (tn.length < 4) return toast.error("Enter a valid tracking number");
    setBusy(true);
    const chosen = carrier === "Other" ? (custom.trim() || "Other") : carrier;
    try {
      await api.post(`/orders/${order.id}/tracking`, { tracking_number: tn, carrier: chosen });
      toast.success("Tracking saved — buyer notified");
      onDone();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  return (
    <div className="ss-modal-back" onClick={onClose}>
      <div className="ss-modal" onClick={(e) => e.stopPropagation()} data-testid="manual-tracking-modal">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
          <h2 style={{ fontSize: 20 }}>Add international tracking</h2>
          <button className="disc-search" style={{ width: 34, height: 34 }} onClick={onClose} data-testid="manual-tracking-close"><X size={18} /></button>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8, background: "var(--gold-soft)", borderRadius: 10, padding: "10px 12px", fontSize: 13, marginBottom: 14 }}>
          <Globe size={15} style={{ color: "var(--gold-2)" }} />
          <span>This order ships to <b>{order.ship_to?.city}{order.ship_to?.country ? `, ${order.ship_to.country}` : ""}</b>. Ship it with your own carrier, then paste the tracking number so the buyer can follow it.</span>
        </div>
        <label className="ss-label">Carrier</label>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 10 }}>
          <div className="ss-field" style={{ height: 46, minWidth: 180, flex: 1 }}>
            <Truck size={16} className="ss-muted" />
            <select value={carrier} onChange={(e) => setCarrier(e.target.value)} data-testid="manual-carrier-select"
              style={{ border: "none", background: "transparent", outline: "none", flex: 1, fontSize: 14, cursor: "pointer" }}>
              {CARRIERS.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          {carrier === "Other" && (
            <div className="ss-field" style={{ height: 46, minWidth: 180, flex: 1 }}>
              <input placeholder="Carrier name (e.g. Saudi Post)" value={custom} onChange={(e) => setCustom(e.target.value)} data-testid="manual-carrier-custom" />
            </div>
          )}
        </div>
        <label className="ss-label">Tracking number</label>
        <div style={{ display: "flex", gap: 8 }}>
          <div className="ss-field" style={{ flex: 1, height: 46 }}>
            <input placeholder="Tracking number" value={tracking} onChange={(e) => setTracking(e.target.value)} data-testid="manual-tracking-input" />
          </div>
          <button className="ss-btn ss-btn-primary" onClick={save} disabled={busy} data-testid="manual-tracking-save">{busy ? "Saving…" : "Save & mark shipped"}</button>
        </div>
      </div>
    </div>
  );
}

function OrderCard({ o, actions }) {
  return (
    <div className="ss-card" style={{ display: "flex", alignItems: "center", gap: 14, padding: 12, flexWrap: "wrap" }} data-testid={`ship-order-${o.id}`}>
      <img src={o.image} alt="" style={{ width: 56, height: 56, borderRadius: 10, objectFit: "cover" }} />
      <div style={{ flex: 1, minWidth: 160 }}>
        <div style={{ fontWeight: 700 }}>{o.item_title}</div>
        <div className="ss-muted" style={{ fontSize: 13, display: "flex", alignItems: "center", gap: 6 }}>
          <MapPin size={12} /> {o.buyer_name}{o.ship_to ? ` · ${o.ship_to.city}${o.ship_to.state ? ", " + o.ship_to.state : (o.international && o.ship_to.country ? ", " + o.ship_to.country : "")}` : ""}
        </div>
        {o.tracking_number && <div className="ss-muted" style={{ fontSize: 12, marginTop: 2 }}>{o.carrier} · {o.tracking_number}</div>}
      </div>
      <div style={{ textAlign: "right" }}>
        <div style={{ fontWeight: 800 }}>{money(o.amount)}</div>
        {o.buyer_shipping_charge > 0 && <div className="ss-muted" style={{ fontSize: 12 }}>+{money(o.buyer_shipping_charge)} ship</div>}
      </div>
      <div style={{ display: "flex", gap: 8, width: "100%", justifyContent: "flex-end", flexWrap: "wrap" }}>{actions}</div>
    </div>
  );
}

export default function Shipments() {
  const navigate = useNavigate();
  const [board, setBoard] = useState(null);
  const [tab, setTab] = useState("awaiting");
  const [rateTarget, setRateTarget] = useState(null);
  const [manualTarget, setManualTarget] = useState(null);
  const [bulkBusy, setBulkBusy] = useState(false);

  const load = () => api.get("/shipping/seller/board").then(setBoard).catch(() => setBoard({ awaiting: [], shipped: [], delivered: [], bundles: [], counts: {}, has_profile: false }));
  useEffect(() => { load(); }, []);
  if (board === null) return <Loading />;

  const voidLabel = async (id) => {
    if (!window.confirm("Void this label? The order returns to Awaiting.")) return;
    try { await api.post("/shipping/void-label", { order_id: id }); toast.success("Label voided"); load(); }
    catch (e) { toast.error(e.message); }
  };

  const markDelivered = async (id) => {
    try { await api.post("/shipping/mark-delivered", { order_id: id }); toast.success("Marked delivered — buyer notified"); load(); }
    catch (e) { toast.error(e.message); }
  };

  const buyAllLabels = async () => {
    if (!window.confirm("Buy labels for all awaiting orders? Items from the same buyer & show will be bundled.")) return;
    setBulkBusy(true);
    try {
      const r = await api.post("/shipping/bulk-label", {});
      toast.success(`${r.labels_created} label${r.labels_created === 1 ? "" : "s"} created${r.bundled_shipments ? ` · ${r.bundled_shipments} bundled` : ""}${r.skipped ? ` · ${r.skipped} skipped` : ""}`);
      load();
    } catch (e) { toast.error(e.message); } finally { setBulkBusy(false); }
  };

  const intlAwaiting = board.awaiting.filter((o) => o.international);
  const domesticBundles = board.bundles.filter((b) => !(b.items || []).some((i) => i.international));
  const bundleIds = new Set(domesticBundles.flatMap((b) => b.order_ids));
  const singles = board.awaiting.filter((o) => !o.international && !bundleIds.has(o.id));

  return (
    <div className="fade-up">
      <PageHead
        title="Shipments"
        sub="Buy labels, bundle live-show orders, print packing slips and track deliveries."
        right={<button className="ss-btn ss-btn-ghost ss-btn-sm" onClick={() => navigate("/shipping-settings")} data-testid="go-shipping-settings"><Settings size={15} /> Shipping settings</button>}
      />

      {!board.has_profile && (
        <div className="ss-card" style={{ padding: 16, marginBottom: 16, borderColor: "var(--gold)", display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }} data-testid="no-profile-banner">
          <Truck size={18} style={{ color: "var(--gold-2)" }} />
          <div style={{ flex: 1 }}>Add your ship-from address so labels use your real origin (using the platform default for now).</div>
          <button className="ss-btn ss-btn-gold ss-btn-sm" onClick={() => navigate("/shipping-settings")} data-testid="setup-profile-btn">Set up now</button>
        </div>
      )}

      <div className="ss-tabs" data-testid="ship-tabs">
        {TABS.map((t) => (
          <button key={t.key} className={`ss-tab ${tab === t.key ? "active" : ""}`} onClick={() => setTab(t.key)} data-testid={`ship-tab-${t.key}`}>
            {t.label}{t.noCount ? "" : ` (${board.counts?.[t.key] ?? 0})`}
          </button>
        ))}
      </div>

      {tab === "awaiting" && (
        (board.awaiting.length === 0) ? (
          <Empty icon={Package} title="Nothing to ship" sub="Paid orders waiting for a label will appear here." />
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {(singles.length + domesticBundles.reduce((n, b) => n + b.order_ids.length, 0)) > 0 && (
              <div style={{ display: "flex", justifyContent: "flex-end" }}>
                <button className="ss-btn ss-btn-gold ss-btn-sm" onClick={buyAllLabels} disabled={bulkBusy} data-testid="bulk-label-btn">
                  <Zap size={15} /> {bulkBusy ? "Buying labels…" : `Buy all US labels (${singles.length + domesticBundles.reduce((n, b) => n + b.order_ids.length, 0)})`}
                </button>
              </div>
            )}
            {domesticBundles.map((b) => (
              <div className="ss-card" style={{ padding: 16, borderColor: "var(--brand)" }} key={b.key} data-testid={`bundle-${b.key}`}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <div className="stat-ic"><Layers size={18} /></div>
                    <div>
                      <div style={{ fontWeight: 800 }}>Bundle · {b.count} items to {b.buyer_name}</div>
                      <div className="ss-muted" style={{ fontSize: 13 }}>Same live show · combined {b.total_weight} lb · save on shipping</div>
                    </div>
                  </div>
                  <button className="ss-btn ss-btn-primary ss-btn-sm" onClick={() => setRateTarget({ order_ids: b.order_ids })} data-testid={`bundle-label-${b.key}`}>
                    <Boxes size={15} /> Buy 1 label
                  </button>
                </div>
                <div style={{ display: "flex", gap: 8, marginTop: 12, flexWrap: "wrap" }}>
                  {b.items.map((i) => (
                    <div key={i.id} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, background: "var(--gold-soft)", padding: "6px 10px", borderRadius: 999 }}>
                      <img src={i.image} alt="" style={{ width: 22, height: 22, borderRadius: 6, objectFit: "cover" }} /> {i.item_title}
                    </div>
                  ))}
                </div>
              </div>
            ))}
            {singles.map((o) => (
              <OrderCard key={o.id} o={o} actions={
                <button className="ss-btn ss-btn-primary ss-btn-sm" onClick={() => setRateTarget({ order_ids: [o.id] })} data-testid={`get-label-${o.id}`}>
                  <Printer size={15} /> Get rates & buy label
                </button>
              } />
            ))}
            {intlAwaiting.length > 0 && (
              <div style={{ marginTop: 4 }} data-testid="intl-awaiting-section">
                <div style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 800, fontSize: 14, margin: "6px 2px 10px" }}>
                  <Globe size={16} style={{ color: "var(--gold-2)" }} /> International orders — add tracking manually
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                  {intlAwaiting.map((o) => (
                    <OrderCard key={o.id} o={o} actions={
                      <button className="ss-btn ss-btn-gold ss-btn-sm" onClick={() => setManualTarget(o)} data-testid={`manual-track-${o.id}`}>
                        <Globe size={15} /> Add carrier & tracking
                      </button>
                    } />
                  ))}
                </div>
              </div>
            )}
          </div>
        )
      )}

      {tab === "shipped" && (
        board.shipped.length === 0 ? (
          <Empty icon={Truck} title="Nothing shipped yet" sub="Orders with a label will appear here." />
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {board.shipped.map((o) => (
              <OrderCard key={o.id} o={o} actions={<>
                {o.tracking_number && <button className="ss-btn ss-btn-ghost ss-btn-sm" onClick={() => navigate(`/track/${o.tracking_number}`)} data-testid={`track-${o.id}`}><Truck size={14} /> Track</button>}
                <button className="ss-btn ss-btn-ghost ss-btn-sm" onClick={() => printLabel(o)} data-testid={`print-label-${o.id}`}><Printer size={14} /> Label</button>
                <button className="ss-btn ss-btn-ghost ss-btn-sm" onClick={() => printSlip(o.id)} data-testid={`print-slip-${o.id}`}><FileText size={14} /> Packing slip</button>
                {!o.bundled_into && <button className="ss-btn ss-btn-primary ss-btn-sm" onClick={() => markDelivered(o.id)} data-testid={`mark-delivered-${o.id}`}><PackageCheck size={14} /> Mark delivered</button>}
                {o.label_id && !o.bundled_into && <button className="ss-btn ss-btn-ghost ss-btn-sm" style={{ color: "var(--red)", borderColor: "var(--red)" }} onClick={() => voidLabel(o.id)} data-testid={`void-${o.id}`}><Ban size={14} /> Void</button>}
              </>} />
            ))}
          </div>
        )
      )}

      {tab === "delivered" && (
        board.delivered.length === 0 ? (
          <Empty icon={Package} title="No deliveries yet" sub="Delivered orders will appear here." />
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {board.delivered.map((o) => (
              <OrderCard key={o.id} o={o} actions={
                o.tracking_number ? <button className="ss-btn ss-btn-ghost ss-btn-sm" onClick={() => navigate(`/track/${o.tracking_number}`)} data-testid={`track-${o.id}`}><Truck size={14} /> View tracking</button> : null
              } />
            ))}
          </div>
        )
      )}

      {tab === "insights" && <InsightsPanel />}

      {rateTarget && <RateModal target={rateTarget} onClose={() => setRateTarget(null)} onDone={() => { setRateTarget(null); load(); }} />}
      {manualTarget && <ManualTrackingModal order={manualTarget} onClose={() => setManualTarget(null)} onDone={() => { setManualTarget(null); load(); }} />}
    </div>
  );
}

function InsightsPanel() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/shipping/seller/insights").then(setData).catch(() => setData({ totals: {}, shipments: [] })); }, []);
  if (data === null) return <Loading />;
  const t = data.totals || {};

  return (
    <div className="fade-up" data-testid="ship-insights">
      <div className="ss-grid grid-4">
        <div className="ss-card stat-card"><div className="stat-ic"><Boxes size={22} /></div><div><div className="stat-val">{t.shipments ?? 0}</div><div className="stat-label">Shipments</div></div></div>
        <div className="ss-card stat-card"><div className="stat-ic"><TrendingUp size={22} /></div><div><div className="stat-val">{money(t.buyer_charged)}</div><div className="stat-label">Buyers charged (shipping)</div></div></div>
        <div className="ss-card stat-card"><div className="stat-ic"><Truck size={22} /></div><div><div className="stat-val">{money(t.carrier_cost)}</div><div className="stat-label">Carrier cost</div></div></div>
        <div className="ss-card stat-card"><div className="stat-ic"><Layers size={22} /></div><div><div className="stat-val">{money(t.platform_markup)}</div><div className="stat-label">Platform markup</div></div></div>
      </div>
      <p className="ss-muted" style={{ fontSize: 13, margin: "12px 2px 20px" }}>
        Shipping is passed through to the carrier at cost. The platform markup covers label handling — it isn't part of your item earnings.
      </p>
      {data.shipments.length === 0 ? (
        <Empty icon={Package} title="No shipments yet" sub="Buy your first label to see shipping insights." />
      ) : (
        <div className="ss-card" style={{ overflow: "auto" }}>
          <table className="ss-table" data-testid="insights-table">
            <thead><tr><th>Item</th><th>Buyer</th><th>Carrier</th><th>Buyer paid</th><th>Carrier cost</th><th>Markup</th><th>Status</th></tr></thead>
            <tbody>
              {data.shipments.map((o) => (
                <tr key={o.id}>
                  <td style={{ fontWeight: 700 }}>{o.item_title}{o.bundle_lead ? " (bundle)" : ""}</td>
                  <td>{o.buyer_name}</td>
                  <td>{o.carrier} {o.service}</td>
                  <td>{money(o.buyer_shipping_charge)}</td>
                  <td>{money(o.shipping_cost)}</td>
                  <td style={{ color: "var(--success)", fontWeight: 700 }}>{money(o.platform_markup)}</td>
                  <td><StatusPill status={o.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
